<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/common/Icon.vue'
import CommonButton from '~/components/common/Button.vue'
import FormPhoneInput from '~/components/common/FormPhoneInput.vue'
import FormInput from '~/components/common/FormInput.vue'
import { Form as VeeForm } from 'vee-validate'
import type { TGeneral } from '~/src/types/common'
import { onClickOutside } from '@vueuse/core'

type TProps = {
  name: string
  modelValue?: string
  label?: string
  canEdit?: boolean
  updated?: boolean
  isEditable?: boolean
  isSubmitting?: boolean
  inputType?: 'phone' | 'text' | 'email'
  placeholder?: string
  valueContainerClasses?: string
  containerClasses?: string
  rules?: string
  originalValue?: string
}

const emit = defineEmits([
  'update:modelValue',
  'updateField',
  'onSubmit',
  'cancelEdit',
])
const props = withDefaults(defineProps<TProps>(), {
  inputType: 'phone',
  placeholder: '',
  valueContainerClasses: '',
  rules: '',
  originalValue: '',
})

const value = computed({
  get: () => props.modelValue,
  set: (val) => {
    emit('update:modelValue', val)
  },
})

const internalValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (newVal: any) => {
    internalValue.value = newVal
  },
  { immediate: true },
)

const hasChanges = computed(() => {
  const originalValue =
    props.originalValue !== undefined
      ? props.originalValue
      : internalValue.value

  if (!originalValue && value.value) return true
  if (originalValue) return value.value !== originalValue
  return false
})

const inputRules = computed(() => {
  if (props.inputType === 'text') return ''
  if (!props.rules) return 'email'
  return props.rules
})

const onSubmit = (values: TGeneral) => {
  emit('onSubmit', {
    key: props.name,
    values: {
      value: values[props.name],
      updated: true,
    },
  })
}

const formRef = ref(null)

const showEditField = () => {
  emit('updateField', {
    key: props.name,
    values: { isEditable: true },
  })
}

const cancelEdit = () => {
  if (props.isEditable && !props.isSubmitting && !props.updated) {
    const originalValue =
      props.originalValue !== undefined
        ? props.originalValue
        : internalValue.value

    emit('update:modelValue', originalValue)

    emit('updateField', {
      key: props.name,
      values: { isEditable: false },
    })

    emit('cancelEdit')
  }
}

onClickOutside(formRef, cancelEdit)
</script>

<template>
  <div :class="[twMerge('flex w-full flex-col gap-y-3.5', containerClasses)]">
    <span v-if="label" class="font-semibold">{{ label }}</span>
    <div
      v-if="!isEditable"
      :class="[
        twMerge(
          'flex items-center justify-between gap-x-2 pl-7 pr-2',
          valueContainerClasses,
        ),
      ]"
    >
      <p class="flex-shrink overflow-hidden text-ellipsis" :title="value">
        {{ value }}
      </p>
      <button
        v-if="canEdit"
        class="ml-auto transition-colors hover:text-p-500"
        @click="showEditField()"
      >
        <Icon name="edit-chart" />
      </button>
    </div>

    <template v-if="canEdit">
      <VeeForm v-slot="{ handleSubmit, meta }" as="">
        <form
          v-show="isEditable && !updated"
          ref="formRef"
          class="h-full w-full"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <FormPhoneInput
            v-if="inputType === 'phone'"
            v-model="value"
            :name="name"
            :rules="rules"
            :is-editable="isEditable"
            :original-value="originalValue"
            :dropdown-options="{
              showCountryName: true,
              showDialCodeInSelection: false,
              showFlags: false,
            }"
            :input-options="{
              showDialCode: false,
            }"
            without-label
            class="flex-grow"
            input-classes="flex flex-row gap-x-1"
          >
            <template #icon-right>
              <CommonButton
                type="submit"
                class="h-full !rounded border border-p-500 bg-p-600 !p-3 text-sm font-semibold disabled:cursor-not-allowed disabled:!border-grey-300 disabled:!bg-grey-300 disabled:!text-grey-400"
                :class="[
                  !meta.valid || !value || isSubmitting || !hasChanges
                    ? '!border-grey-400'
                    : 'border-p-500',
                ]"
                :show-spinner="isSubmitting"
                :disabled="!meta.valid || !value || isSubmitting || !hasChanges"
              >
                Save
              </CommonButton>
            </template>
            <template #arrow-icon>
              <Icon class="size-4" name="ChevronDown" />
            </template>
          </FormPhoneInput>
          <div v-else class="flex flex-row gap-x-1">
            <FormInput
              v-model="value"
              :name="name"
              :type="inputType"
              :rules="inputRules"
              class="flex-grow rounded border border-p-500"
              border-classes-input="!border-none !gap-x-1 h-full focus-within:!ring-none focus-within:!border-none"
              label-classes="!border-none"
              input-classes="h-full"
            >
            </FormInput>
            <CommonButton
              type="submit"
              class="h-full !rounded border bg-p-600 !p-3 text-sm font-semibold disabled:cursor-not-allowed disabled:!border-grey-300 disabled:!bg-grey-300 disabled:!text-grey-400"
              :class="[
                !meta.valid || !value || isSubmitting || !hasChanges
                  ? '!border-grey-400'
                  : 'border-p-500',
              ]"
              :show-spinner="isSubmitting"
              :disabled="!meta.valid || !value || isSubmitting || !hasChanges"
            >
              Save
            </CommonButton>
          </div>
        </form>
      </VeeForm>

      <div
        v-if="updated"
        class="flex h-[42px] items-center justify-between gap-x-1"
      >
        <span
          class="flex h-full flex-grow items-center justify-start overflow-hidden text-ellipsis rounded border border-good pl-3"
        >
          {{ value }}
        </span>
        <div
          class="flex h-full flex-shrink-0 items-center justify-center rounded border border-good bg-good px-5"
        >
          <Icon class="size-5" name="check-circle" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
:deep(.vue-tel-input) {
  @apply border-none !important;
}

:deep(.vue-tel-input:focus-within) {
  @apply border-none ring-0 ring-offset-0 !important;
}

:deep(.vue-tel-input .vti__dropdown) {
  @apply w-full max-w-[30%] rounded bg-s-200 px-3 py-2.5 text-sm font-semibold;

  color: #23252c;
}

:deep(.vue-tel-input .vti__dropdown .vti__selection) {
  @apply flex items-center justify-between gap-x-1.5 text-sm;
}

:deep(.vue-tel-input .vti__phone) {
  border: 1px solid !important;

  @apply rounded border border-p-500 px-3 py-2 !important;
}
</style>
